import React from 'react';
import { t } from '../utils/i18n';
import Spacing from '../components/common/Spacing';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import { navbarLayout } from '../layouts/navbarLayout';
import H from '../components/elements/H';
import Breadcrumbs, { BreadcrumbItem } from '../components/head/Breadcrumbs';
import Container from '../components/grid/Container';
import HorizontalLine from '../components/common/HorizontalLine';
import { CmsMainHelpPage } from '../integrations/contentful/types';
import HeadingWithCount from '../components/common/HeadingWithCount';
import HelpCategoryItem from '../components/help-pages/HelpCategoryItem';
import { fetchMainHelpPage } from '../integrations/contentful/queries';
import ContactCard from '../components/help-pages/ContactCard';
import CollaborationCard from '../components/help-pages/CollaborationCard';
import Col from '../components/grid/Col';
import Row from '../components/grid/Row';
import DisplayOn from '../components/common/DisplayOn';
import SmallHelpCategoryItem from '../components/help-pages/SmallHelpCategoryItem';
import StickyCol from '../components/grid/StickyCol';
import { columnTopSpacing } from '../styles/theme';
import { TimPage } from './TimPage';

export const helpPageBreadcrumbItem: BreadcrumbItem = {
  name: t('Tuki'),
  path: Paths.DestinationList
};

interface HelpPageProps {
  helpPage?: CmsMainHelpPage;
}

const HelpPage: TimPage<HelpPageProps> = ({ helpPage }) => {
  if (!helpPage) {
    return null;
  }
  const helpCategories = helpPage.fields.categories;
  return (
    <div data-cy="help-page">
      <SEO
        title={helpPage.fields.seoTitle || helpPage.fields.title}
        canonicalPath={generatePath(Paths.Help)}
        description={helpPage.fields.seoDescription || helpPage.fields.ingress}
      />
      <Breadcrumbs items={[helpPageBreadcrumbItem]} />

      <Container>
        <Row>
          <Col size={[24, 24, 7]}>
            <StickyCol contentTopOffset={columnTopSpacing.sideColumn}>
              <Spacing bottom={5}>
                <Spacing bottom={1}>
                  <H styling="h5" color="gray">
                    {t('Tietoa palvelusta')}
                  </H>
                </Spacing>
                <H styling="h2">
                  <strong>{helpPage.fields.title}</strong>
                </H>
              </Spacing>
              <DisplayOn large>
                <SmallHelpCategoryItem
                  path={Paths.Help}
                  text={t('Etusivu')}
                  selected
                />
                {helpCategories.map((category) => {
                  return (
                    <Spacing top={1} key={category.sys.id}>
                      <SmallHelpCategoryItem
                        path={Paths.HelpCategory}
                        slug={category.fields.slug}
                        text={category.fields.title}
                      />
                    </Spacing>
                  );
                })}
              </DisplayOn>
            </StickyCol>
          </Col>
          <Col size={[0, 0, 1]}></Col>
          <Col size={[24, 24, 16]}>
            <Spacing bottom={7} top={columnTopSpacing.contentColumn}>
              <CollaborationCard />
            </Spacing>
            <Spacing bottom={3}>
              <H styling="h3" tag="div">
                {helpPage.fields.ingress}
              </H>
            </Spacing>
            <HorizontalLine />
            <Spacing bottom={[11, 7, 7]} top={7}>
              <ContactCard />
            </Spacing>
            <DisplayOn small medium>
              <HorizontalLine />
            </DisplayOn>
            <Spacing top={[4, 6, 6]} bottom={4}>
              <HeadingWithCount
                icon="travel-question"
                text={t('Aihealueet')}
                renderCount={() => helpCategories.length}
                data-testid="category-count"
              />
            </Spacing>
            <Spacing bottom={30}>
              <Row>
                {helpCategories.map((category) => {
                  return (
                    <Col key={category.sys.id} size={[24, 12, 12]}>
                      <Spacing bottom={4}>
                        <HelpCategoryItem helpCategory={category} />
                      </Spacing>
                    </Col>
                  );
                })}
              </Row>
            </Spacing>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

HelpPage.getLayout = (page) =>
  navbarLayout(
    <DisplayOn small medium>
      <H styling="h4" tag="div">
        <strong>{t('Tietoa palvelusta')}</strong>
      </H>
    </DisplayOn>,
    page
  );
HelpPage.getInitialProps = async function () {
  const helpPage = await fetchMainHelpPage();
  const httpErrorCode = !helpPage ? 404 : undefined;
  return {
    helpPage,
    httpErrorCode
  };
};

export default HelpPage;
